import { clstx } from '@payaca/utilities/cvaMerge';
import React, {
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useMemo,
} from 'react';
import { Box, PolymorphicComponentProps } from 'react-polymorphic-box';

/**
 *
 * Usage:
 * <Card>
 *   <Card.Header>Header</Card.Header>
 *   <Card.Body>
 *      <Card.Title>Title</Card.Title>
 *      <Card.Subtitle>Subtitle</Card.Subtitle>
 *      <p>Body text</p>
 *   </Card.Body>
 *   <Card.Footer>Footer</Card.Footer>
 * </Card>
 *
 */

type CardSizeVariantLiteral = 'xs' | 'sm' | 'md' | 'lg';

const SizeContext = React.createContext<CardSizeVariantLiteral>('md');

export interface CardBaseProps {
  sizeVariant?: CardSizeVariantLiteral;
  variant?: 'default' | 'error';
}

export type CardProps<E extends React.ElementType> = PolymorphicComponentProps<
  E,
  CardBaseProps
>;

const defaultElement = 'div';

const Card: <E extends React.ElementType = typeof defaultElement>(
  props: CardProps<E>
) => React.ReactNode | null = React.forwardRef(
  <E extends React.ElementType = typeof defaultElement>(
    {
      children,
      sizeVariant = 'md',
      variant = 'default',
      className,
      ...rest
    }: CardProps<E>,
    ref: typeof rest.ref
  ) => {
    return (
      <SizeContext.Provider value={sizeVariant}>
        {/*It seems `react-polymorphic-box` is deprecated. And isn't compatible with typescript v5*/}
        {/*https://github.com/kripod/react-polymorphic-box/issues/26#issuecomment-2014522158*/}
        {/*@ts-expect-error*/}
        <Box
          as={defaultElement}
          className={clstx(
            'typography:prose flex flex-col rounded-xl border bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:shadow-gray-700/[.7]',
            variant === 'error' && 'border-red-500',
            className
          )}
          ref={ref}
          {...rest}
        >
          {children}
        </Box>
      </SizeContext.Provider>
    );
  }
);

const Header: FunctionComponent = ({ children }: PropsWithChildren) => {
  return (
    <div className="rounded-t-xl border-b bg-gray-100 px-4 py-3 dark:border-gray-700 dark:bg-gray-800 md:px-5 md:py-4">
      <p className="mt-1">{children}</p>
    </div>
  );
};

const Footer: FunctionComponent<PropsWithChildren> = ({
  children,
}: PropsWithChildren) => {
  return (
    <div className="rounded-b-xl border-t bg-gray-100 px-4 py-3 dark:border-gray-700 dark:bg-gray-800 md:px-5 md:py-4">
      <p className="mt-1">{children}</p>
    </div>
  );
};

const Title: FunctionComponent<PropsWithChildren<{ className?: string }>> = ({
  children,
  className,
}) => {
  return <h3 className={className}>{children}</h3>;
};

const Subtitle: FunctionComponent<
  PropsWithChildren<{ className?: string }>
> = ({ children, className }) => {
  return (
    <p className={'mt-1 uppercase' + (className ? ' ' + className : '')}>
      {children}
    </p>
  );
};

const Body: FunctionComponent<PropsWithChildren<{ className?: string }>> = ({
  children,
  className,
}) => {
  const sizeVariant = useContext(SizeContext);

  const sizeClasses = useMemo(() => {
    switch (sizeVariant) {
      case 'xs':
        return 'p-2 md:p-2';
      case 'lg':
        return 'md:p-7';
      case 'md':
        return 'md:p-5';
    }
  }, [sizeVariant]);

  return <div className={clstx('p-4', sizeClasses, className)}>{children}</div>;
};

export default Object.assign(Card, {
  Body,
  Header,
  Footer,
  Title,
  Subtitle,
});
